/* eslint-disable react/jsx-filename-extension */
import React from 'react';
//Components
import NavBar from './components/Navigation/Main-Navigation/main-navigation';
import Footer from './components/Footer/Footer';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
//Context
import { InternalSsoProvider } from '@baltlife/internal-sso-react';
import { sso } from './auth/sso';
import { UserProvider } from './context/UserContext';

//Utilities
import { UserAuthentication } from './components/Authentication/UserAuthentication/UserAuthentication';
import { OAuthCallback } from './components/Authentication/OAuthCallback/OAuthCallback';
import { SignOut } from './components/Authentication/SignOut/SignOut';
import { Index as ErrorIndex } from './pages/Error/Index/Index';
import { datadogRum } from '@datadog/browser-rum';
import { ThemeProvider, createTheme } from '@mui/material/styles';

import { Outlet, useRoutes } from 'react-router-dom';

import './App.css';
import routez from './components/Utilities/Router/routes';

const queryClient = new QueryClient({
  defaultOptions: { queries: { refetchOnWindowFocus: false } },
});

// MUI v6 mutates the default theme during render; with Vite's frozen ESM
// imports that throws "theme is read-only". Pass a created theme so MUI has
// a mutable object to attach to.
const theme = createTheme();

datadogRum.init({
  applicationId: '73689a27-edab-4235-9a09-ddf02659fec9',
  clientToken: 'pub3f9cbf498c15ee629e6152cd1d2fadd9',
  site: 'datadoghq.com',
  service: 'agent-portal-production',
  env: 'prod',
  version: '1.0.0',
  sampleRate: 100,
  trackUserInteractions: true,
  defaultPrivacyLevel: 'allow',
});

datadogRum.startSessionReplayRecording();

// The authenticated app shell. It is the element of the layout route below, so it only
// ever renders as a descendant of the guard and of UserProvider — by the time it runs,
// useUser() has a populated user and NavBar/Footer can read user.permissions safely.
// The routed page renders through <Outlet />.
//
// Defined at MODULE scope on purpose. A component declared inside App's body would get a
// fresh function identity on every App render, and React would unmount and remount this
// whole subtree — nav, page and all — on every navigation.
function AppShell() {
  return (
    <div className="App">
      <NavBar />
      <div className="container mt-2 mb-2">
        <main role="main" className="pb-1">
          <Outlet />
        </main>
      </div>
      <Footer />
    </div>
  );
}

function App() {
  // Which routes need a session is expressed by the route tree, not by anything reading
  // window.location during render. /oauth/callback, /Error/* and /SignOut/* are SIBLINGS
  // of the authenticated subtree, so neither the guard nor UserProvider is an ancestor of
  // them: the code→token exchange, the error pages and sign-out all run with no session.
  //
  // The layout route carrying the guard is deliberately PATHLESS. routez() returns
  // absolute paths (/Home, /Admin/*), and react-router rejects an absolute child path
  // under a parent that has one; a pathless parent contributes no path segment, so the
  // whole table nests unchanged.
  const routes = useRoutes([
    { path: '/oauth/callback', element: <OAuthCallback /> },
    { path: '/Error/*', element: <ErrorIndex /> },
    { path: '/SignOut/*', element: <SignOut /> },
    {
      element: (
        <UserAuthentication>
          <UserProvider>
            <AppShell />
          </UserProvider>
        </UserAuthentication>
      ),
      children: routez(),
    },
  ]);

  return (
    <ThemeProvider theme={theme}>
      <InternalSsoProvider sso={sso}>
        <QueryClientProvider client={queryClient}>{routes}</QueryClientProvider>
      </InternalSsoProvider>
    </ThemeProvider>
  );
}

export default App;
